import { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

type UseListItemPreviewArgs<T extends { id: number }> = {
    items: T[];
    routeId: number | null;
    currentId: number | null | undefined;
    pathPrefix: string;
    enabled?: boolean;
    onPreview: (item: T) => void;
};

/**
 * Keeps the selected list item preview in sync with the URL.
 * Click selects instantly (preview + navigate); a later fetch can enrich the detail.
 */
const useListItemPreview = <T extends { id: number }>({
    items,
    routeId,
    currentId,
    pathPrefix,
    enabled = true,
    onPreview,
}: UseListItemPreviewArgs<T>) => {
    const navigate = useNavigate();
    const onPreviewRef = useRef(onPreview);
    onPreviewRef.current = onPreview;

    const itemsById = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

    useEffect(() => {
        if (!enabled || routeId == null || currentId === routeId) return;
        const match = itemsById.get(routeId);
        if (match) onPreviewRef.current(match);
    }, [currentId, enabled, itemsById, routeId]);

    const selectItem = useCallback(
        (item: T) => {
            if (item.id === routeId) return;
            onPreviewRef.current(item);
            navigate(`${pathPrefix}/${item.id}`);
        },
        [navigate, pathPrefix, routeId],
    );

    return { selectItem };
};

export default useListItemPreview;
